import React from 'react';
import { gql } from 'urql';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { useAuthenticatedQuery } from '../../../client/hooks';
import {
    PlayerMyEventsDocument,
    PlayerMyEventsQuery,
    PlayerMyEventsQueryVariables,
} from '../../../generated/spin-graphql';
import { MyEventsCard } from './MyEventsCard';
import { MyUpcomingEvents } from './MyUpcomingEvents';
import { PlayerMyEventsSkeleton } from './PlayerMyEventsSkeleton';

type Props = {
    user: AppStateUser;
};

gql`
    query PlayerMyEvents($id: String!, $playerPrimaryGame: espin_game_definition_enum, $hasPrimary: Boolean!) {
        admin_recommended_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }
            where: { admin_recommended: { _eq: true }, status: { _eq: published }, event_end_date: { _gte: "now()" } }
        ) {
            ...MyUpcomingEvents
        }
        recommended_events: espin_event(
            limit: 2

            order_by: { event_start_date: asc }

            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_games: { game: { game_definition: { _eq: $playerPrimaryGame } } }
            }
        ) @include(if: $hasPrimary) {
            ...MyUpcomingEvents
        }
        any_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }

            where: { event_end_date: { _gte: "now()" }, status: { _eq: published } }
        ) @skip(if: $hasPrimary) {
            ...MyUpcomingEvents
        }
        registered_events: espin_event(
            order_by: { event_start_date: asc }

            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_registrations: {
                    _or: [
                        { user_id: { _eq: $id } }
                        { team: { members: { player_profile: { user_id: { _eq: $id } } } } }
                    ]
                }
            }
        ) {
            ...MyEventsCard
        }
    }
`;

export const PlayerMyEvents: React.FC<Props> = ({ user }) => {
    const primaryGame = user.player_profile?.player_games.find((g) => g.primary === true)?.game.game_definition;
    const { isReady, data } = useAuthenticatedQuery<PlayerMyEventsQuery, PlayerMyEventsQueryVariables>({
        query: PlayerMyEventsDocument,
        variables: {
            id: user.id,
            playerPrimaryGame: user.player_profile?.player_games.find((g) => g.primary === true)?.game.game_definition,
            hasPrimary: !!primaryGame,
        },
    });

    if (!isReady) {
        return <PlayerMyEventsSkeleton />;
    }

    const { recommended_events, registered_events, any_events } = data;
    const upcoming_events = recommended_events ?? any_events;

    return (
        <div className="my-events">
            <MyEventsCard events={registered_events} />
            <MyUpcomingEvents events={upcoming_events} />
        </div>
    );
};
